/* ==========================================================================
   CLARITY SCIENCE — KEY MINERALS ENGINE
   Tabs, the SVG diagram and the stacked blocks are three views of one index.
   Changing it costs a handful of style writes and one Web-Animations call —
   no per-frame JavaScript and no scroll handler; scroll position is read by an
   IntersectionObserver. Data (name + mg) comes from the section's blocks via a
   JSON payload, so the dose readout and arc length are real, editable data.
   Per-instance + Theme-Editor safe.
   ========================================================================== */
(function () {
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var ARC_C = 653.45; /* circumference of the r=104 dose ring */

  function initMinerals(root) {
    if (!root || root.dataset.clarityMineralsBound === "1") return;
    var qsa = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };

    var tabs = qsa(".kn__tab");
    var nodes = qsa(".mviz .node");
    var items = qsa("[data-kn-item]");
    if (!tabs.length || !items.length) return;
    root.dataset.clarityMineralsBound = "1";

    var viz = root.querySelector(".mviz");
    var konst = viz && viz.querySelector(".const");
    var arc = viz && viz.querySelector(".arc");
    var dose = viz && viz.querySelector(".dose");
    var nameEl = viz && viz.querySelector(".aname");
    var pulse = viz && viz.querySelector(".pulse");

    /* Data straight off the supplement facts panel (emitted from blocks). The
       arc length is each value against the largest, so the ring is real data. */
    var DATA = [];
    var payload = root.querySelector("[data-clarity-minerals]");
    if (payload) { try { DATA = JSON.parse(payload.textContent) || []; } catch (e) { DATA = []; } }
    if (!DATA.length) {
      /* Fallback: read from the nodes themselves. */
      DATA = nodes.map(function (n) {
        return { name: (n.getAttribute("data-name") || "").toUpperCase(), mg: parseFloat(n.getAttribute("data-mg")) || 0 };
      });
    }
    var N = DATA.length || 1;
    var MAXMG = DATA.reduce(function (m, d) { return Math.max(m, d.mg || 0); }, 0) || 1;
    var STEP = 360 / N;

    root.classList.add("kn--live");
    var cur = -1, rot = 0;

    /* Turn the constellation so mineral i arrives at the top, always the shorter
       way round, accumulating so it never snaps back through zero. */
    var spin = function (i) {
      var target = -STEP * i;
      var delta = ((target - rot) % 360 + 540) % 360 - 180;
      rot += delta;
      if (viz) viz.style.setProperty("--rot", rot);
    };

    var show = function (i) {
      if (i === cur) return;
      var first = cur === -1;
      cur = i;

      var mark = function (t, on) {
        t.classList.toggle("is-on", on);
        if (on) { t.setAttribute("aria-current", "true"); }
        else { t.removeAttribute("aria-current"); }
      };
      tabs.forEach(function (t, k) {
        mark(t, k === i);
        t.tabIndex = k === i ? 0 : -1; /* roving tabindex across the jump buttons */
      });
      nodes.forEach(function (t, k) { mark(t, k === i); });
      items.forEach(function (t, k) { t.classList.toggle("is-on", k === i); });

      if (!viz) return;
      spin(i);
      if (arc) arc.style.strokeDashoffset = (ARC_C * (1 - (DATA[i].mg || 0) / MAXMG)).toFixed(1);

      /* Swap the two readouts under cover of a short fade. */
      var write = function () {
        if (nameEl) nameEl.textContent = DATA[i].name || "";
        if (dose) dose.textContent = (DATA[i].mg != null ? DATA[i].mg + " MG" : "");
      };
      if (reduce || first) { write(); }
      else {
        viz.classList.add("is-swapping");
        setTimeout(function () { write(); viz.classList.remove("is-swapping"); }, 170);
      }

      /* A single halo that expands once on each change. */
      if (!reduce && pulse && pulse.animate) {
        pulse.animate(
          [{ transform: "scale(1)", opacity: 0.55 }, { transform: "scale(1.85)", opacity: 0 }],
          { duration: 1100, easing: "cubic-bezier(.22,.61,.36,1)" }
        );
      }
    };

    var goTo = function (i) {
      show(i);
      items[i].scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
    };

    tabs.forEach(function (t, i) {
      t.addEventListener("click", function () { goTo(i); });
      t.addEventListener("keydown", function (e) {
        var to = null;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") to = (i + 1) % N;
        else if (e.key === "ArrowLeft" || e.key === "ArrowUp") to = (i - 1 + N) % N;
        else if (e.key === "Home") to = 0;
        else if (e.key === "End") to = N - 1;
        if (to === null) return;
        e.preventDefault();
        show(to);
        if (tabs[to]) tabs[to].focus();
      });
    });
    nodes.forEach(function (t, i) {
      t.addEventListener("click", function () { goTo(i); });
      t.addEventListener("mouseenter", function () { show(i); });
      t.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goTo(i); }
      });
    });

    if ("IntersectionObserver" in window) {
      /* Whichever block sits in the middle band of the screen is the active one. */
      var spy = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) show(+e.target.dataset.knItem); });
      }, { rootMargin: "-45% 0px -45% 0px", threshold: 0 });
      items.forEach(function (el) { spy.observe(el); });

      /* Orbits only turn while the section is on screen. */
      var live = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { root.classList.toggle("is-live", e.isIntersecting); });
      }, { rootMargin: "140px 0px", threshold: 0 });
      live.observe(root);
    } else {
      root.classList.add("is-live");
    }
    show(0);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll("[data-clarity-minerals-root]"), initMinerals);
  }

  if (document.readyState !== "loading") scan(document);
  else document.addEventListener("DOMContentLoaded", function () { scan(document); });

  document.addEventListener("shopify:section:load", function (e) { scan(e.target); });
})();
